import { useState, useEffect, useCallback } from 'react'

const ADMIN_PASSWORD = 'caleffi-acquisti-2026'
const API_URL = process.env.REACT_APP_API_URL || 'https://api.caleffiweb.it/api/v1/'
const ADMIN_KEY = 'caleffi-admin-2026'

// ─── Interfaces ────────────────────────────────────────────────────────────────

interface InvoiceSummary {
  invoice_number: string
  created_at: string
  total_records: number
}

interface InvoiceRecord {
  id: number
  invoice_number: string
  order_number: string
  article_code: string
  custom_code: string
  description: string
  quantity: number
  price: number
  currency: string
  production_lot: string
  number_of_box: number
  number_pallets: number
  gross_weight: number
  net_weight: number
  shipping_mode: string
  invoice_date: string
  expected_delivery_date: string
}

interface Attachment {
  label: string
  filename: string
  path: string
}

interface InvoiceDetail {
  records: InvoiceRecord[]
  attachments: Attachment[]
}

interface UserStat {
  id: number
  username: string
  email: string
  rag_soc: string
  supplier_code: string[]
  lastLoginAt: string | null
  createdAt: string
  totalInvoices: number
  exportedInvoices: number
  invoices: InvoiceSummary[]
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (d: string | null) =>
  d ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

const fmtShort = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '—'

const DOC_LABELS: Record<string, string> = {
  invoice_pdf: '📄 Fattura PDF',
  packing_list: '📦 Packing List',
  atr: '📋 ATR',
  cmr: '🚚 CMR',
  bill_of_lading: '🚢 Bill of Lading',
  other: '📎 Allegato',
}

// ─── CSV Export ────────────────────────────────────────────────────────────────

function exportToCSV(records: InvoiceRecord[], invoiceNumber: string) {
  const headers = [
    'N° Fattura', 'Cod. Articolo', 'Custom Code', 'Descrizione', 'N° Ordine',
    'Quantità', 'Prezzo', 'Valuta', 'Lotto', 'N° Colli', 'N° Pallet',
    'Peso Lordo', 'Peso Netto', 'Spedizione', 'Data Fattura', 'Consegna Prevista'
  ]
  const rows = records.map(r => [
    r.invoice_number, r.article_code, r.custom_code, r.description, r.order_number,
    r.quantity, r.price, r.currency, r.production_lot,
    r.number_of_box, r.number_pallets, r.gross_weight, r.net_weight,
    r.shipping_mode, fmtShort(r.invoice_date), fmtShort(r.expected_delivery_date)
  ])
  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `fattura_${invoiceNumber}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function exportToXLSX(records: InvoiceRecord[], invoiceNumber: string, ragSoc: string) {
  // Build a simple HTML table that Excel can open
  const headers = [
    'N° Fattura', 'Fornitore', 'Cod. Articolo', 'Custom Code', 'Descrizione', 'N° Ordine',
    'Quantità', 'Prezzo', 'Valuta', 'Lotto', 'N° Colli', 'N° Pallet',
    'Peso Lordo', 'Peso Netto', 'Spedizione', 'Data Fattura', 'Consegna Prevista'
  ]
  const rows = records.map(r => [
    r.invoice_number, ragSoc, r.article_code, r.custom_code, r.description, r.order_number,
    r.quantity, r.price, r.currency, r.production_lot,
    r.number_of_box, r.number_pallets, r.gross_weight, r.net_weight,
    r.shipping_mode, fmtShort(r.invoice_date), fmtShort(r.expected_delivery_date)
  ])

  const table = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="UTF-8"/><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Fattura ${invoiceNumber}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml></head>
<body><table>
<tr>${headers.map(h => `<th style="background:#009ef7;color:#fff;font-weight:bold">${h}</th>`).join('')}</tr>
${rows.map(row => `<tr>${row.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`).join('\n')}
</table></body></html>`

  const blob = new Blob([table], { type: 'application/vnd.ms-excel;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `fattura_${invoiceNumber}.xls`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function UfficioAcquistiPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordError, setPasswordError] = useState(false)

  const [users, setUsers] = useState<UserStat[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [expandedUser, setExpandedUser] = useState<number | null>(null)
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null)
  const [invoiceDetails, setInvoiceDetails] = useState<Record<string, InvoiceDetail>>({})
  const [loadingInvoice, setLoadingInvoice] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  // ── API calls ─────────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${API_URL}admin/users-stats`, {
        headers: { Authorization: `Bearer ${ADMIN_KEY}` }
      })
      if (!res.ok) throw new Error(`Errore HTTP ${res.status}`)
      const data = await res.json()
      setUsers(data.data || [])
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (authenticated) fetchUsers()
  }, [authenticated, fetchUsers])

  const fetchInvoiceDetail = async (invoiceNumber: string) => {
    if (expandedInvoice === invoiceNumber) { setExpandedInvoice(null); return }
    if (invoiceDetails[invoiceNumber]) { setExpandedInvoice(invoiceNumber); return }
    setLoadingInvoice(invoiceNumber)
    try {
      const res = await fetch(
        `${API_URL}admin/invoice-detail/${encodeURIComponent(invoiceNumber)}`,
        { headers: { Authorization: `Bearer ${ADMIN_KEY}` } }
      )
      if (!res.ok) throw new Error(`Errore HTTP ${res.status}`)
      const data = await res.json()
      setInvoiceDetails(prev => ({ ...prev, [invoiceNumber]: data.data }))
      setExpandedInvoice(invoiceNumber)
    } catch (e: any) {
      alert('Errore caricamento dettaglio: ' + e.message)
    } finally {
      setLoadingInvoice(null)
    }
  }

  const downloadDoc = (filePath: string, filename: string) => {
    fetch(`${API_URL}admin/download-document?filePath=${encodeURIComponent(filePath)}`, {
      headers: { Authorization: `Bearer ${ADMIN_KEY}` }
    })
      .then(r => { if (!r.ok) throw new Error('File non trovato'); return r.blob() })
      .then(blob => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = filename
        document.body.appendChild(a); a.click()
        document.body.removeChild(a); URL.revokeObjectURL(url)
      })
      .catch(e => alert('Errore download: ' + e.message))
  }

  // ── Auth screen ───────────────────────────────────────────────────────────

  if (!authenticated) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa' }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '48px 40px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', width: 380, textAlign: 'center' }}>
          <h2 style={{ fontWeight: 700, marginBottom: 8, color: '#1a1a2e' }}>Ufficio Acquisti</h2>
          <p style={{ color: '#888', marginBottom: 24, fontSize: 14 }}>Portale riservato — inserire la password</p>
          <form onSubmit={e => {
            e.preventDefault()
            if (passwordInput === ADMIN_PASSWORD) { setAuthenticated(true); setPasswordError(false) }
            else setPasswordError(true)
          }}>
            <input
              type="password" placeholder="Password" value={passwordInput}
              onChange={e => setPasswordInput(e.target.value)} autoFocus
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: passwordError ? '1.5px solid #e74c3c' : '1.5px solid #ddd', fontSize: 15, marginBottom: 8, outline: 'none', boxSizing: 'border-box' }}
            />
            {passwordError && <p style={{ color: '#e74c3c', fontSize: 13, marginBottom: 8 }}>Password errata</p>}
            <button type="submit" style={{ width: '100%', padding: '11px', background: '#009ef7', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
              Accedi
            </button>
          </form>
        </div>
      </div>
    )
  }

  // ── Main page ─────────────────────────────────────────────────────────────

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase()
    return !q || u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) ||
      u.rag_soc?.toLowerCase().includes(q) || u.supplier_code?.some(c => c.toLowerCase().includes(q))
  })

  const totalFatture = users.reduce((s, u) => s + u.totalInvoices, 0)
  const recentLogins = users.filter(u => u.lastLoginAt && (Date.now() - new Date(u.lastLoginAt).getTime()) < 30 * 86400000).length

  return (
    <div style={{ minHeight: '100vh', background: '#f5f8fa', padding: '32px 24px' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>

        {/* ── Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontWeight: 700, fontSize: 26, color: '#1a1a2e', margin: 0 }}>📊 Ufficio Acquisti</h1>
            <p style={{ color: '#888', margin: '4px 0 0', fontSize: 14 }}>Fornitori iscritti · riepilogo fatture · esportazione dati</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={fetchUsers} style={{ padding: '8px 18px', background: '#009ef7', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>↻ Aggiorna</button>
            <button onClick={() => setAuthenticated(false)} style={{ padding: '8px 18px', background: '#f1f3f5', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Esci</button>
          </div>
        </div>

        {/* ── Stats cards */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
          {[
            { label: 'Fornitori totali', value: users.length, color: '#009ef7', icon: '🏭' },
            { label: 'Fatture totali', value: totalFatture, color: '#50cd89', icon: '📄' },
            { label: 'Login ultimi 30gg', value: recentLogins, color: '#ffc700', icon: '🔑' },
          ].map(c => (
            <div key={c.label} style={{ background: '#fff', borderRadius: 10, padding: '20px 28px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flex: '1 1 160px', borderTop: `4px solid ${c.color}` }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: c.color }}>{c.icon} {c.value}</div>
              <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* ── Search */}
        <input
          type="text" placeholder="🔍 Cerca per nome, email, ragione sociale, codice fornitore…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid #ddd', fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 16 }}
        />

        {/* ── Table */}
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          {loading && <div style={{ textAlign: 'center', padding: 48, color: '#888' }}>⏳ Caricamento in corso…</div>}
          {error && <div style={{ textAlign: 'center', padding: 48, color: '#e74c3c' }}>❌ Errore: {error}</div>}

          {!loading && !error && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
                  {['Ragione Sociale', 'Email', 'Cod. Fornitore', 'Registrazione', 'Ultimo Accesso', 'Fatture', ''].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#555', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 48, color: '#aaa' }}>Nessun fornitore trovato</td></tr>
                )}

                {filteredUsers.map(user => (
                  <>
                    {/* ── Supplier row */}
                    <tr key={user.id} style={{ borderBottom: '1px solid #f0f0f0', background: expandedUser === user.id ? '#f0f7ff' : undefined, transition: 'background .15s' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#1a1a2e' }}>{user.rag_soc || user.username}</td>
                      <td style={{ padding: '12px 16px', color: '#555' }}>{user.email}</td>
                      <td style={{ padding: '12px 16px' }}>
                        {user.supplier_code?.map(c => (
                          <span key={c} style={{ background: '#e8f4fd', color: '#009ef7', borderRadius: 4, padding: '2px 8px', fontSize: 12, marginRight: 4, display: 'inline-block' }}>{c}</span>
                        ))}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#888', whiteSpace: 'nowrap', fontSize: 13 }}>{fmt(user.createdAt)}</td>
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontSize: 13 }}>
                        {user.lastLoginAt
                          ? <span style={{ color: '#50cd89', fontWeight: 500 }}>{fmt(user.lastLoginAt)}</span>
                          : <span style={{ color: '#ccc' }}>Mai</span>}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{ background: user.totalInvoices > 0 ? '#e8f8f0' : '#f5f5f5', color: user.totalInvoices > 0 ? '#50cd89' : '#aaa', borderRadius: 20, padding: '3px 12px', fontWeight: 700, fontSize: 13 }}>
                          {user.totalInvoices}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {user.totalInvoices > 0 && (
                          <button
                            onClick={() => { setExpandedUser(expandedUser === user.id ? null : user.id); setExpandedInvoice(null) }}
                            style={{ background: 'none', border: '1.5px solid #009ef7', color: '#009ef7', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {expandedUser === user.id ? '▲ Chiudi' : '▼ Vedi fatture'}
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* ── Invoice list for this supplier */}
                    {expandedUser === user.id && (
                      <tr key={`${user.id}-inv`}>
                        <td colSpan={7} style={{ padding: 0, background: '#f7fbff' }}>
                          <div style={{ padding: '16px 32px 20px' }}>
                            <div style={{ fontWeight: 600, marginBottom: 12, color: '#333' }}>
                              Fatture di <strong>{user.rag_soc || user.username}</strong> — {user.totalInvoices} fattur{user.totalInvoices === 1 ? 'a' : 'e'}
                            </div>

                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                              <thead>
                                <tr style={{ background: '#e8f4fd' }}>
                                  <th style={{ padding: '8px 14px', textAlign: 'left', color: '#555', fontWeight: 600 }}>N° Fattura</th>
                                  <th style={{ padding: '8px 14px', textAlign: 'left', color: '#555', fontWeight: 600 }}>Data caricamento</th>
                                  <th style={{ padding: '8px 14px', textAlign: 'center', color: '#555', fontWeight: 600 }}>Righe</th>
                                  <th style={{ padding: '8px 14px', textAlign: 'center', color: '#555', fontWeight: 600 }}>Azioni</th>
                                </tr>
                              </thead>
                              <tbody>
                                {user.invoices?.map((inv, idx) => (
                                  <>
                                    {/* ── Invoice row */}
                                    <tr key={inv.invoice_number} style={{ borderBottom: '1px solid #f0f0f0', background: expandedInvoice === inv.invoice_number ? '#fffbe6' : idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                      <td style={{ padding: '8px 14px', fontWeight: 600 }}>📄 {inv.invoice_number}</td>
                                      <td style={{ padding: '8px 14px', color: '#888' }}>{fmt(inv.created_at)}</td>
                                      <td style={{ padding: '8px 14px', textAlign: 'center' }}>
                                        <span style={{ background: '#f0f0f0', borderRadius: 12, padding: '2px 10px', fontSize: 12 }}>{inv.total_records}</span>
                                      </td>
                                      <td style={{ padding: '8px 14px', textAlign: 'center' }}>
                                        <button
                                          onClick={() => fetchInvoiceDetail(inv.invoice_number)}
                                          disabled={loadingInvoice === inv.invoice_number}
                                          style={{ background: 'none', border: '1.5px solid #ffc700', color: '#b8860b', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                          {loadingInvoice === inv.invoice_number ? '⏳' : expandedInvoice === inv.invoice_number ? '▲ Chiudi' : '▼ Dettaglio'}
                                        </button>
                                      </td>
                                    </tr>

                                    {/* ── Invoice detail */}
                                    {expandedInvoice === inv.invoice_number && invoiceDetails[inv.invoice_number] && (
                                      <tr key={`${inv.invoice_number}-detail`}>
                                        <td colSpan={4} style={{ padding: '0 0 8px 0', background: '#fffdf0' }}>
                                          <div style={{ padding: '14px 20px' }}>

                                            {/* Export buttons */}
                                            {invoiceDetails[inv.invoice_number].records.length > 0 && (
                                              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                                <button
                                                  onClick={() => exportToCSV(invoiceDetails[inv.invoice_number].records, inv.invoice_number)}
                                                  style={{ background: '#50cd89', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                  ⬇ Esporta CSV
                                                </button>
                                                <button
                                                  onClick={() => exportToXLSX(invoiceDetails[inv.invoice_number].records, inv.invoice_number, user.rag_soc || user.username)}
                                                  style={{ background: '#1d6f42', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                  ⬇ Esporta XLSX
                                                </button>
                                                <span style={{ color: '#888', fontSize: 12, lineHeight: '28px' }}>
                                                  {invoiceDetails[inv.invoice_number].records.length} rig{invoiceDetails[inv.invoice_number].records.length === 1 ? 'a' : 'he'}
                                                </span>
                                              </div>
                                            )}

                                            {/* Group-by Custom Code summary */}
                                            {invoiceDetails[inv.invoice_number].records.length > 0 && (() => {
                                              type Summary = { custom_code: string; qty: number; amount: number; currency: string; net: number; gross: number }
                                              const summaryMap = new Map<string, Summary>()
                                              invoiceDetails[inv.invoice_number].records.forEach(r => {
                                                const key = r.custom_code || '(non specificato)'
                                                const cur = summaryMap.get(key)
                                                if (!cur) {
                                                  summaryMap.set(key, { custom_code: key, qty: Number(r.quantity), amount: Number(r.price) * Number(r.quantity), currency: r.currency || '', net: Number(r.net_weight), gross: Number(r.gross_weight) })
                                                } else {
                                                  cur.qty += Number(r.quantity)
                                                  cur.amount += Number(r.price) * Number(r.quantity)
                                                  cur.net += Number(r.net_weight)
                                                  cur.gross += Number(r.gross_weight)
                                                }
                                              })
                                              const summaryRows = Array.from(summaryMap.values())
                                              return (
                                                <div style={{ marginBottom: 16 }}>
                                                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#555', fontSize: 12 }}>📊 Riepilogo per Custom Code</div>
                                                  <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', background: '#f0f7ff', borderRadius: 6, overflow: 'hidden' }}>
                                                    <thead>
                                                      <tr style={{ background: '#009ef7' }}>
                                                        {['Custom Code', 'Quantità tot.', 'Importo tot.', 'Val.', 'Peso Netto tot.', 'Peso Lordo tot.'].map(h => (
                                                          <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                                                        ))}
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {summaryRows.map((s, i) => (
                                                        <tr key={s.custom_code} style={{ background: i % 2 === 0 ? '#fff' : '#e8f4fd', borderBottom: '1px solid #d0e8f8' }}>
                                                          <td style={{ padding: '5px 10px', fontWeight: 600 }}>{s.custom_code}</td>
                                                          <td style={{ padding: '5px 10px', textAlign: 'right' }}>{s.qty.toLocaleString('it-IT')}</td>
                                                          <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 600 }}>{s.amount.toFixed(2)}</td>
                                                          <td style={{ padding: '5px 10px' }}>{s.currency}</td>
                                                          <td style={{ padding: '5px 10px', textAlign: 'right' }}>{s.net.toFixed(2)}</td>
                                                          <td style={{ padding: '5px 10px', textAlign: 'right' }}>{s.gross.toFixed(2)}</td>
                                                        </tr>
                                                      ))}
                                                      {/* Totals row */}
                                                      <tr style={{ background: '#d0e8f8', fontWeight: 700, borderTop: '2px solid #009ef7' }}>
                                                        <td style={{ padding: '5px 10px' }}>TOTALE</td>
                                                        <td style={{ padding: '5px 10px', textAlign: 'right' }}>{summaryRows.reduce((s, r) => s + r.qty, 0).toLocaleString('it-IT')}</td>
                                                        <td style={{ padding: '5px 10px', textAlign: 'right' }}>{summaryRows.reduce((s, r) => s + r.amount, 0).toFixed(2)}</td>
                                                        <td style={{ padding: '5px 10px' }}>{summaryRows[0]?.currency || ''}</td>
                                                        <td style={{ padding: '5px 10px', textAlign: 'right' }}>{summaryRows.reduce((s, r) => s + r.net, 0).toFixed(2)}</td>
                                                        <td style={{ padding: '5px 10px', textAlign: 'right' }}>{summaryRows.reduce((s, r) => s + r.gross, 0).toFixed(2)}</td>
                                                      </tr>
                                                    </tbody>
                                                  </table>
                                                </div>
                                              )
                                            })()}

                                            {/* Records table */}
                                            {invoiceDetails[inv.invoice_number].records.length > 0 && (
                                              <div style={{ overflowX: 'auto', marginBottom: 14 }}>
                                                <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: 900 }}>
                                                  <thead>
                                                    <tr style={{ background: '#f0f0f0' }}>
                                                      {['Cod. Art.', 'Custom Code', 'Descrizione', 'N° Ord.', 'Qtà', 'Prezzo', 'Val.', 'Lotto', 'Colli', 'Pallet', 'P. Lordo', 'P. Netto', 'Spedizione', 'Data Fatt.', 'Cons. Prev.'].map(h => (
                                                        <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#666', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '2px solid #ddd' }}>{h}</th>
                                                      ))}
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {invoiceDetails[inv.invoice_number].records.map((r, i) => (
                                                      <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #eee' }}>
                                                        <td style={{ padding: '5px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.article_code || '—'}</td>
                                                        <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{r.custom_code || '—'}</td>
                                                        <td style={{ padding: '5px 8px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.description}>{r.description || '—'}</td>
                                                        <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{r.order_number || '—'}</td>
                                                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.quantity}</td>
                                                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{Number(r.price).toFixed(2)}</td>
                                                        <td style={{ padding: '5px 8px' }}>{r.currency || '—'}</td>
                                                        <td style={{ padding: '5px 8px' }}>{r.production_lot || '—'}</td>
                                                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.number_of_box}</td>
                                                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.number_pallets}</td>
                                                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.gross_weight}</td>
                                                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.net_weight}</td>
                                                        <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{r.shipping_mode}</td>
                                                        <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{fmtShort(r.invoice_date)}</td>
                                                        <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{fmtShort(r.expected_delivery_date)}</td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                            )}

                                            {/* Attachments */}
                                            {invoiceDetails[inv.invoice_number].attachments.length > 0 && (
                                              <div>
                                                <div style={{ fontWeight: 600, marginBottom: 8, color: '#555', fontSize: 12 }}>📎 Documenti allegati</div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                  {invoiceDetails[inv.invoice_number].attachments.map((att, ai) => (
                                                    <button key={ai} onClick={() => downloadDoc(att.path, att.filename)}
                                                      style={{ background: '#fff', border: '1.5px solid #50cd89', color: '#2e7d52', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                      {DOC_LABELS[att.label] || '📎 ' + att.label} &nbsp;⬇
                                                    </button>
                                                  ))}
                                                </div>
                                              </div>
                                            )}

                                            {invoiceDetails[inv.invoice_number].records.length === 0 && invoiceDetails[inv.invoice_number].attachments.length === 0 && (
                                              <div style={{ color: '#aaa', fontStyle: 'italic', fontSize: 13 }}>Nessun dato disponibile per questa fattura</div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p style={{ textAlign: 'center', color: '#ccc', fontSize: 12, marginTop: 24 }}>
          Caleffi S.p.A. — Portale Fornitori © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  )
}
